import React, { useEffect, useState } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { useHistory } from "react-router-dom";
import { apiService } from "lib/apiService";

const YearlyCreditDebitChart = ({ years, currency, selectedGlobalYear }) => {
  const history = useHistory();
  const [selectedYear, setSelectedYear] = useState(selectedGlobalYear || new Date().getFullYear());
  const [data, setData] = useState({ credit: [], debit: [] });
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    setSelectedYear(selectedGlobalYear); // Sync with global year when it changes
  }, [selectedGlobalYear]);

  useEffect(() => {
    // Check if user is not logged in (localStorage is empty)
    if (!localStorage.getItem("userData")) {
      // Redirect to the login page
      history.push("/login");
    }
  }, [history]);

  //get userid form loca storage
  const storedUserData = localStorage.getItem("userData");
  let userId = "";
  let groupId = "";
  if (storedUserData) {
    const userData = JSON.parse(storedUserData);
    userId = userData.userId;
    groupId = userData.groupId;
  }

  // Detect dark mode
  useEffect(() => {
    const checkDarkMode = () =>
      document.documentElement.classList.contains("dark");
    setIsDarkMode(checkDarkMode());

    const observer = new MutationObserver(() => {
      setIsDarkMode(checkDarkMode());
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  // Fetch yearly data
  useEffect(() => {
    if (!groupId) return;
    apiService.getYearlyCreditDebit(groupId, selectedYear).then(setData);
  }, [selectedYear, groupId]);

  const latestTimestamp = Math.max(
    ...(data.credit.map((d) => d[0])),
    ...(data.debit.map((d) => d[0]))
  );

  const baseDate =
    latestTimestamp > 0
      ? new Date(latestTimestamp)
      : new Date(selectedYear, 11, 1);

  const threeMonthsAgo = new Date(
    baseDate.getFullYear(),
    baseDate.getMonth() - 2,
    1
  ).getTime();

  const options = {
    chart: {
      height: 400,
      backgroundColor: "transparent",
      style: {
        color: isDarkMode ? "#f3f4f6" : "#111827",
      },
    },
    title: { text: undefined },
    xAxis: {
      type: "datetime",
      min: threeMonthsAgo,
      labels: {
        style: {
          color: isDarkMode ? "#d1d5db" : "#374151",
        },
      },
      lineColor: isDarkMode ? "#374151" : "#d1d5db",
      tickColor: isDarkMode ? "#374151" : "#d1d5db",
    },
    yAxis: {
      opposite: false,
      title: { text: `Amount (${currency})` },
      labels: {
        formatter() {
          return `${currency}${Highcharts.numberFormat(this.value, 0, '.', ',')}`;
        },
        style: {
          color: isDarkMode ? "#d1d5db" : "#374151",
        },
      },
      gridLineColor: isDarkMode ? "#1f2937" : "#e5e7eb",
    },
    tooltip: {
      shared: true,
      pointFormatter() {
        return `<span style="color:${this.color}">\u25CF</span> <b>${this.series.name}: ${currency}${Highcharts.numberFormat(this.y, 0, '.', ',')}</b><br/>`;
      },
      backgroundColor: isDarkMode ? "#111827" : "#ffffff",
      borderColor: isDarkMode ? "#374151" : "#d1d5db",
      style: {
        color: isDarkMode ? "#f3f4f6" : "#111827",
        fontSize: "13px",
      },
    },
    plotOptions: {
      series: {
        marker: {
          enabled: true,
          radius: 3,
          states: { hover: { radius: 5 } },
          symbol: "circle",
        },
        dataLabels: { enabled: false },
      },
      spline: {
        states: { hover: { lineWidth: 4 } },
        marker: { enabled: true, radius: 3, symbol: "circle" },
      },
    },
    series: [
      {
        name: "Credit",
        data: data.credit.map(([x, y]) => ({ x, y, marker: { enabled: y > 0 } })),
        type: "spline",
        color: "#10B981", // green
        lineWidth: 2,
      },
      {
        name: "Debit",
        data: data.debit.map(([x, y]) => ({ x, y, marker: { enabled: y > 0 } })),
        type: "spline",
        color: "#EF4444", // red
        lineWidth: 2,
      },
    ],
    credits: { enabled: false },
    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
      itemStyle: {
        color: isDarkMode ? "#f3f4f6" : "#111827",
        fontSize: "13px",
      },
      itemHoverStyle: {
        color: "#10B981",
      },
    },
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-4">
      <div className="flex justify-between items-center mb-4 flex-row lg:flex-row sm:flex-col">
        <h2 className="text-gray-800 dark:text-white font-semibold text-lg">
          Yearly Credit vs Debit ({selectedYear})
        </h2>
        <select
          className="mt-2 sm:mt-0 text-xs text-gray-800 dark:text-white rounded-md px-3 py-2"
          style={{ minWidth: "80px", border: "solid #ddd 1px" }}
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      <HighchartsReact
        highcharts={Highcharts}
        constructorType="stockChart"
        options={options}
      />

      <div className="flex justify-center gap-4 mt-4 text-sm">
        <div className="flex items-center gap-1">
          <span className="rounded-full inline-block" style={{backgroundColor:"#10B981", height:"10px", width:"10px"}}></span>
          Credit
        </div>
        <div className="flex items-center gap-1">
          <span className="rounded-full inline-block" style={{backgroundColor:"#EF4444", height:"10px", width:"10px"}}></span>
          Debit
        </div>
      </div>
    </div>
  );
};

export default YearlyCreditDebitChart;